<template>
  <v-card :class="compact && 'elevation-0'">
    <v-card-title class="text-center headline mx-auto" data-cy="typeview-title">
      <h4>{{ t('donationType.text') }}</h4>
    </v-card-title>
    <v-card-text :class="compact && 'pa-0'">
      <v-row>
        <v-col
          v-for="donationType in donationTypesComputed"
          :key="donationType.id"
          cols="12"
          sm="6"
          class="d-flex"
        >
          <v-hover v-slot="hover">
            <div class="d-flex w-100">
              <v-card
                v-bind="hover?.props"
                :id="'donationtype-card_' + donationType.name"
                :data-cy="'donationtype-card_' + donationType.name"
                :elevation="hover?.isHovering ? 10 : 4"
                :aria-label="t(donationType.nameTranslationKey)"
                class="pa-4 text-center donation-type-card"
                tabindex="0"
                role="button"
                @click="donationTypeClick(donationType)"
                @keydown.enter.space.prevent="donationTypeClick(donationType)"
              >
                <v-btn
                  :id="'donationtype-button_' + donationType.name"
                  :color="donationType.color"
                  :data-cy="'donationtype-button_' + donationType.name"
                  tabindex="-1"
                  class="ma-4"
                  fab
                  size="large"
                  variant="outlined"
                  rounded="circle"
                  style="width: 64px; height: 64px; min-width: 64px; max-width: 64px"
                >
                  <v-icon size="large" color="dark">
                    {{ donationType.icon }}
                  </v-icon>
                </v-btn>
                <h3 class="text-center mb-4">
                  {{ t(donationType.nameTranslationKey) }}
                </h3>
                <div class="flex-grow-1 d-flex align-center">
                  <p class="ma-auto text-center dt-text">
                    {{ t(donationType.descriptionTranslationKey) }}
                  </p>
                </div>
              </v-card>
            </div>
          </v-hover>
        </v-col>
      </v-row>
    </v-card-text>
    <donation-flow-buttons
      :show-previous="showBackButton"
      :show-next="showNextButton"
      previous-data-cy="donation-type-back-btn"
      next-data-cy="donation-type-next-btn"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, computed, watch, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useLabel } from '@/composables/useLabel';
import { DonationType } from '@/classes/DonationType';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { stepV2 } from '@/enums/donationStepOrder';
import { useStepperFlow } from '@/composables/useStepperFlow';
import store from '@/store';
import matomo from '@/services/matomoService';

export default defineComponent({
  components: {
    DonationFlowButtons,
  },
  // replaced StepperFlowMixin with composable useStepperFlow
  props: {
    donationTypes: {
      type: Array as () => Array<DonationType>,
      default: () => [],
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    showNextButton: {
      type: Boolean,
      default: false,
    },
    compact: Boolean,
  },
  emits: ['selectDonationType', 'removeStep'],
  setup(props, { emit }) {
    const { t } = useLabel();

    useStepperFlow();

    const display = useDisplay();

    const steps = computed(() => stepV2);

    const donation = computed(() => store.state.donation);
    const currentStep = computed(() => store.state.currentStep);
    const requestSession = computed(() => store.state.requestSession);

    const donationTypesComputed = computed(() => {
      if (requestSession.value?.organizationGroup) {
        // Hardcode order: MEMORY_GIFT first, then CONTRIBUTION
        const memoryGift = props.donationTypes.find((dt) => dt.type === 'MEMORY_GIFT');
        const contribution = props.donationTypes.find((dt) => dt.type === 'CONTRIBUTION');
        return [memoryGift, contribution].filter(Boolean);
      }
      return props.donationTypes;
    });

    const donationTypeClick = (donationType: DonationType) => {
      matomo.trackEvent('Donation Type', 'Selected', donationType.type);
      // Emit selection to the parent flow component which will switch versions/steps synchronously
      emit('selectDonationType', donationType);
    };

    watch(
      currentStep,
      (newVal) => {
        if (newVal === steps.value.DONATION_TYPE) {
          matomo.trackEvent('Donation Type', 'Viewed', 'Type Selection Screen');
          // Only auto-skip if there's exactly one donation type
          if (donationTypesComputed.value && donationTypesComputed.value.length === 1) {
            emit('removeStep', steps.value.DONATION_TYPE);
            const donationType = donationTypesComputed.value[0];
            if (donationType) {
              donationTypeClick(donationType);
            }
          }
        }
      },
      { immediate: true }
    );

    return {
      t,
      steps,
      display,
      donation,
      currentStep,
      donationTypeClick,
      donationTypesComputed,
    };
  },
});
</script>

<style scoped lang="scss">
.headline {
  max-width: 400px;
  word-break: normal;
  white-space: normal;
  padding: 16px;
  hyphens: none;
}

.donation-type-card {
  width: 100%;
  border-radius: 16px;
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
}

.dt-text {
  width: 100%;
  max-width: 245px;
  max-height: 6.25rem; /* ~100px */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
}
[role='button']:focus {
  outline: 2px solid var(--primary-color-highlight);
  outline-offset: 2px;
}
</style>
